import { useState, useEffect } from 'react';
import { X, Github, Loader, CheckCircle } from 'lucide-react';
import { githubService } from '../../services/githubService';

export default function GithubConnectModal({ isOpen, onClose, onConnected }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);

  useEffect(() => {
    if (isOpen) {
      initializeAuth();
    }
  }, [isOpen]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Initializing GitHub auth...');
      const url = await githubService.getAuthUrl();
      console.log('GitHub auth URL received:', url);
      setAuthUrl(url);
    } catch (err) {
      console.error('GitHub initialization error:', err);
      setError(err.message || 'Failed to initialize GitHub connection. Please try again.');
      // Reset the modal state on error
      setStep(1);
      setAuthUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (!authUrl) {
      setError('GitHub authorization URL is not available. Please try again.');
      return;
    }
    console.log('Redirecting to GitHub auth URL:', authUrl);
    window.location.href = authUrl;
  };

  const handleCallback = async (code, state) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Processing GitHub callback...');
      await githubService.handleCallback(code, state);
      console.log('GitHub callback processed successfully');
      setStep(3);
      onConnected();
    } catch (err) {
      console.error('GitHub callback error:', err);
      setError(err.message || 'Failed to complete GitHub integration. Please try again.');
      // Reset to step 1 on error
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for callback code and state in URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state && isOpen) {
      setStep(2);
      handleCallback(code, state);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
      <div className="fixed inset-0" onClick={onClose} />
      
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Github size={24} className="text-primary" />
              <h2 className="text-xl font-semibold">Connect GitHub</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 text-danger rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <p className="text-text-secondary">
                    Connect your GitHub account to analyze code review patterns and detect AI usage in your repositories.
                  </p>
                  <ul className="list-disc list-inside text-text-secondary space-y-1">
                    <li>Read-only access to your repositories</li>
                    <li>Analyze code review patterns</li>
                    <li>Detect AI usage in pull requests</li>
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleConnect}
                    disabled={loading || !authUrl}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      loading || !authUrl
                        ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                        : 'bg-primary text-background hover:bg-primary-light'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <Loader size={16} className="animate-spin" />
                        Initializing...
                      </div>
                    ) : (
                      'Connect GitHub'
                    )}
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader size={32} className="animate-spin text-primary mb-4" />
                <p className="text-text-secondary">Completing GitHub integration...</p>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle size={48} className="text-primary mb-4" />
                <h3 className="text-lg font-medium mb-2">GitHub Connected Successfully</h3>
                <p className="text-text-secondary text-center mb-6">
                  Your GitHub account has been connected. You can now analyze code review patterns and detect AI usage in your repositories.
                </p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 